import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import LeaveTypeSerializer from 'issara-sdk/types/LeaveTypeSerializer_apps_HRM';


// API
import LeaveTypeList from "issara-sdk/apis/LeaveTypeList_apps_HRM";
import LeaveTypeDetail from "issara-sdk/apis/LeaveTypeDetail_apps_HRM";

export type State = Partial<{
  // seq
  LeavePolicySequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    leaveTypeList: LeaveTypeSerializer[] | LeaveTypeListApiType[];
    selectedType: string;
    leaveSettings: LeaveTypeSerializer | LeaveTypeListApiType;
  }> | null;
}>;

// * on Usage
export type LeaveTypeListApiType = Partial<{
  id?: number;
  type: "S" | "B" | "A" | "M" | string; // "S" = Sick, "B" = "Business", "A" = Annual, "M" = Maternity
  name: string;
  max_leave_days: string; // decimal in Python
  depend_on_experience: boolean;
  conditions: LeaveTypeConditionType[];
  restrict_for_gender: "A" | "M" | "F"; // "A" = All, "M" = Male, "F" = Female
  attachment_required: boolean;
  advance_notice: boolean;
  threshold_advance_day?: number;
  allow_retroactive_leave: boolean;
  threshold_retroactive_day?: number;
  allow_half_day_leave: boolean;
  restart_counting_every_year: boolean;
  maximum_accrued_leave_days: number; // decimal in Python
  reset_day?: number;
  reset_month?: number;
  active?: boolean;
}>;

export type LeaveTypeConditionType = {
  experience_years: number | string; // decimal in Python
  max_leave_days: number | string; // decimal in Python
};

export type Props = {};

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage" | "masterOptions">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "LeavePolicy";
  restart?: boolean;
  clear?: boolean;
};

// Action
type ActionType = { action: "FETCH" } | { action: "SAVE"; saveLeaveType: LeaveTypeListApiType; isCreate: boolean };

type SeqAct = ActionType & SeqState;

type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  LeavePolicySequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event =
  | { message: "RunSequence"; params: {} }
  | { message: "GetMasterData"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardLeavePolicy";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  FETCH: "FETCH",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

const master = [] as const;

export const RestrictGenderOptions = [
  { key: "A", value: "A", text: "All" },
  { key: "M", value: "M", text: "Male" },
  { key: "F", value: "F", text: "Female" },
];

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      LeavePolicySequence: {
        ...state.LeavePolicySequence,
        sequenceIndex: "Action",
      },
    },
    () => Action(controller, { ...params, action: "FETCH" })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.FETCH]: HandleFetchLeaveType,
    [ACTIONS.SAVE]: HandleSaveLeaveType,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

/**========================================================================
 **                              HANDLERS
 *========================================================================**/

const HandleFetchLeaveType: Handler<Params<"FETCH">> = async (controller, params) => {
  const state = controller.getState();

  const [result, err] = await GetLeavePolicyList(controller, params);

  controller.setState({
    LeavePolicySequence: {
      ...state.LeavePolicySequence,
      leaveTypeList: result.items,
    },
  });
};

const HandleSaveLeaveType: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  const { saveLeaveType, isCreate } = params;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SAVE]: "LOADING",
    },
  });

  if (!saveLeaveType.name) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "Please specify the leave type name",
      },
    });

    return;
  }

  const [result, err] = await CreateNUpdateLeavePolicy(controller, { data: saveLeaveType, isCreate });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "Save Leave Type Success",
      },
    });
  }

  HandleFetchLeaveType(controller, { action: "FETCH" });
};

/**========================================================================
 **                               API
 *========================================================================**/

const GetLeavePolicyList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await LeaveTypeList.list({
    apiToken: controller.apiToken,
    params: {
      type: params.type,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const CreateNUpdateLeavePolicy: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = params?.isCreate ? 
    await LeaveTypeList.create({
      apiToken: controller.apiToken,
      data: {
        ...params.data,
      },
      extra: {
        division: controller.data.division,
      },
    })
    : await LeaveTypeDetail.update({
      apiToken: controller.apiToken,
      pk: params.data?.id,
      data: {
        ...params.data,
      },
      extra: {
        division: controller.data.division,
      },
    });

  if (err) return [null, err];

  return [resp, null];
};
